import { useEffect, useState } from "react";
import api from "../lib/api";
import { TrendUp, Users, Car, House, Coins, Crown } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("de-DE").format(n || 0);
const fmtCompact = (n) => {
  if (!n) return "0";
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

export default function ServerStatsWidget() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/public/server-stats").then(r => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="border border-tactical-border bg-black/60 backdrop-blur-xl overflow-hidden" data-testid="server-stats-widget">
      <div className="px-4 py-2.5 border-b border-tactical-border flex items-center justify-between">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-white/60">
          <span className="w-1.5 h-1.5 bg-tactical-success animate-pulse" /> Server Economy · Live
        </div>
        <div className="font-mono text-[10px] text-white/40">ROUTINE.RP</div>
      </div>

      {/* Economy headline */}
      <div className="p-5 border-b border-tactical-border">
        <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest mb-1">Gesamt-Wirtschaftsvolumen</div>
        <div className="flex items-baseline gap-3">
          <div className="font-mono font-black text-4xl md:text-5xl text-volt" data-testid="economy-total">
            ${fmtCompact(stats?.total_economy)}
          </div>
          <div className="font-mono text-xs text-white/40">
            ${fmt(stats?.total_economy)}
          </div>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 font-mono text-xs">
          <MiniRow label="Cash" value={fmtCompact(stats?.total_cash)} tone="text-tactical-success" />
          <MiniRow label="Bank" value={fmtCompact(stats?.total_bank)} tone="text-volt" />
          <MiniRow label="Schwarz" value={fmtCompact(stats?.total_black_money)} tone="text-tactical-danger" />
        </div>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-3 divide-x divide-tactical-border">
        <CountCell icon={Users} label="Spieler" value={stats?.total_players ?? "—"} />
        <CountCell icon={Car} label="Fahrzeuge" value={stats?.total_vehicles ?? "—"} />
        <CountCell icon={House} label="Immobilien" value={stats?.total_properties ?? "—"} />
      </div>

      {/* Top earners */}
      {stats?.top_earners?.length > 0 && (
        <div className="p-4 border-t border-tactical-border">
          <div className="flex items-center gap-2 font-mono text-[10px] text-white/40 uppercase tracking-widest mb-2">
            <Crown size={12} className="text-tactical-warn" /> Top Earners (Cash)
          </div>
          <div className="space-y-1.5">
            {stats.top_earners.slice(0, 3).map((c, i) => (
              <div key={i} className="flex items-center justify-between font-mono text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`w-4 text-center font-bold ${i === 0 ? "text-tactical-warn" : i === 1 ? "text-white/70" : "text-white/50"}`}>#{i + 1}</span>
                  <span className="text-white truncate">{c.first_name} {c.last_name}</span>
                  <span className="text-white/40 text-[10px] truncate">· {c.job_grade_label || "—"}</span>
                </div>
                <span className="text-tactical-success flex-shrink-0">${fmtCompact(c.cash)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MiniRow({ label, value, tone }) {
  return (
    <div className="border border-tactical-border/60 p-2">
      <div className="text-[9px] text-white/40 uppercase tracking-widest">{label}</div>
      <div className={`font-bold text-sm ${tone}`}>${value}</div>
    </div>
  );
}

function CountCell({ icon: Icon, label, value }) {
  return (
    <div className="p-4 text-center">
      <Icon size={16} className="text-white/50 mx-auto mb-1.5" />
      <div className="font-mono font-bold text-lg">{value}</div>
      <div className="font-mono text-[9px] text-white/40 uppercase tracking-widest">{label}</div>
    </div>
  );
}
